import type {
  Breadcrumb,
  BrowserError,
  ErrorTrace,
  FrontendTransaction,
  IncomingError,
  ResolvedConfig,
  TransactionBreadcrumb,
} from "./types.js";
import { DEFAULT_SERVICE_NAME } from "./types.js";
import { getSessionContext, getTags } from "./session.js";
import { addBreadcrumb, getErrorBreadcrumbs } from "./breadcrumbs.js";
import { sendError } from "./transport.js";
import { getRouteTemplate } from "./vitals.js";
import type { RequestResult } from "./network-hook.js";
import { scrubPageUrl, scrubUrl, safeUrl, globMatch, stripTrailingSlash, errorLike, pruneRecordForJson, applyHook, logError, attemptCleanup } from "./utils.js";

// Subscribers fired after an error has cleared every gate (sample_rate,
// beforeError, dedupe) and been handed to transport. Other modules
// subscribe instead of being imperatively poked from inside handleError —
// same pattern as onBeforeNavigation, onAfterRequest, etc.
const errorListeners: ((event: BrowserError) => void)[] = [];

export function onErrorReported(fn: (event: BrowserError) => void): () => void {
  errorListeners.push(fn);
  return () => {
    const i = errorListeners.indexOf(fn);
    if (i >= 0) errorListeners.splice(i, 1);
  };
}

let config: ResolvedConfig["errors"];
let appVersion: string | undefined;
let serviceName: string;
let beforeErrorHook: ((event: IncomingError) => IncomingError | null) | undefined;
// Query-param allowlist for scrubbing URLs that ride the error payload. The
// errors module captures `location.href` for `environment.url`; without this it
// would ship raw query params (tokens, emails, OAuth fragments) — bypassing the
// privacy gate every other capture path goes through.
let allowlist: string[] = [];
let networkBlocklist: string[] = [];
// A request error that reached transport. Permanent: a report for that object
// exists, so no later route may send a second one.
let reportedRequestErrors = new WeakSet<object>();
// A request error the SDK held back for volume, and the time the hold expires.
// The gates that cause it are windowed, so the hold has to be windowed too, or
// a ten second storm would silence the object for the life of the page.
let suppressedRequestErrors = new WeakMap<object, number>();

function trackable(error: unknown): error is object {
  return error !== null && (typeof error === "object" || typeof error === "function");
}

function alreadyReported(error: unknown): boolean {
  return trackable(error) && reportedRequestErrors.has(error);
}

/** True while a route that the host did not ask for must stay quiet. An
 * explicit `captureError` is the host asking, so it does not consult this. */
function handledRecently(error: unknown): boolean {
  if (alreadyReported(error)) return true;
  if (!trackable(error)) return false;
  const until = suppressedRequestErrors.get(error);
  return until !== undefined && Date.now() < until;
}

// Error click tracking — exported so breadcrumbs can check for recent errors
let lastErrorTimestamp = 0;
export function getLastErrorTimestamp(): number {
  return lastErrorTimestamp;
}

interface DedupeEntry {
  key: string;
  count: number;
  firstSeen: number;
}

let dedupeWindow: DedupeEntry[] = [];
const DEDUPE_MAX_COUNT = 5;
const DEDUPE_WINDOW_MS = 10_000;

// Global rate limit, independent of dedupe. Dedupe only caps *identical*
// errors (5 per key); a loop emitting errors with ever-changing messages
// (counters, ids, timestamps in the text) bypasses it entirely and would
// otherwise fire one network POST per error and grow dedupeWindow without
// bound. The global cap bounds total errors entering the pipeline per window
// regardless of key — 100 / 10s is far above any sane app's real error rate
// but stops a runaway loop from making the SDK the source of the problem.
const RATE_LIMIT_MAX = 100;
const RATE_LIMIT_WINDOW_MS = 10_000;
let rateWindowStart = 0;
let rateWindowCount = 0;

/** How long a held-back request error stays quiet on the routes the host did
 * not ask for. The gates that hold it back are windowed, so this matches the
 * longer of them. */
const SUPPRESSION_HOLD_MS = Math.max(RATE_LIMIT_WINDOW_MS, DEDUPE_WINDOW_MS);

function rateLimited(now: number): boolean {
  if (now - rateWindowStart >= RATE_LIMIT_WINDOW_MS) {
    rateWindowStart = now;
    rateWindowCount = 0;
  }
  rateWindowCount++;
  return rateWindowCount > RATE_LIMIT_MAX;
}

let errorHandler: ((event: ErrorEvent) => void) | null = null;
let rejectionHandler: ((event: PromiseRejectionEvent) => void) | null = null;

export function initErrors(
  resolved: ResolvedConfig["errors"],
  queryParamsAllowlist: string[],
  version?: string,
  beforeError?: (event: IncomingError) => IncomingError | null,
  service: string = DEFAULT_SERVICE_NAME,
  blockedRequests: string[] = [],
): void {
  destroyErrors();

  config = resolved;
  allowlist = queryParamsAllowlist;
  appVersion = version;
  serviceName = service;
  networkBlocklist = blockedRequests;
  beforeErrorHook = beforeError;

  errorHandler = (event: ErrorEvent) => {
    if (handledRecently(event.error)) return;
    handleError(
      event.message,
      event.filename,
      event.lineno,
      event.colno,
      event.error?.stack,
      undefined,
      event.error?.name,
    );
  };
  window.addEventListener("error", errorHandler);

  rejectionHandler = (event: PromiseRejectionEvent) => {
    const reason = event.reason;
    if (handledRecently(reason)) return;
    // errorLike, not `instanceof Error`, so a rejection carrying an error from
    // another realm keeps its message, class and stack instead of collapsing to
    // "{}" — which also made every such rejection share one dedupe key.
    const asError = errorLike(reason);
    const message = asError ? asError.message : stringifyReason(reason);
    handleError(
      message,
      undefined,
      undefined,
      undefined,
      asError?.stack,
      undefined,
      asError?.name,
    );
  };
  window.addEventListener("unhandledrejection", rejectionHandler);
}

export function destroyErrors(): void {
  if (errorHandler) {
    const handler = errorHandler;
    errorHandler = null;
    attemptCleanup("error listener", () => window.removeEventListener("error", handler));
  }
  if (rejectionHandler) {
    const handler = rejectionHandler;
    rejectionHandler = null;
    attemptCleanup("rejection listener", () => window.removeEventListener("unhandledrejection", handler));
  }
  dedupeWindow = [];
  rateWindowStart = 0;
  rateWindowCount = 0;
  lastErrorTimestamp = 0;
  errorListeners.length = 0;
  reportedRequestErrors = new WeakSet();
  suppressedRequestErrors = new WeakMap();
}

/** Report an error through the full pipeline. Used by captureError for framework plugins. */
export function reportError(
  error: Error,
  context?: Record<string, unknown>,
): void {
  if (alreadyReported(error)) return;
  handleError(
    error.message,
    undefined,
    undefined,
    undefined,
    error.stack,
    context,
    error.name,
  );
}

/** Report a request the backend can answer for, as the parent span the request
 * sent. Later application errors never claim this identity. */
export function reportRequestError(result: RequestResult): void {
  if (!result.trace || result.aborted) return;
  // Only a failure the backend can answer for. A timeout usually means the
  // server took the request and traced it, though a deadline can expire during
  // DNS or connect. Every other transport failure (refused, undeliverable,
  // blocked by an extension) leaves no span to join, and its volume follows the
  // device's connection rather than the backend.
  const serverError = result.status !== undefined && result.status >= 500 && result.status <= 599;
  if (!serverError && !result.timedOut) return;
  // A deadline missed on an offline device says nothing about the backend, and
  // a page that keeps polling would report one failure per attempt.
  if (result.timedOut && navigator.onLine === false) return;
  const parsed = safeUrl(result.url);
  if (!parsed || networkBlocklist.some((pattern) => globMatch(pattern, parsed.host + parsed.pathname))) return;
  const url = scrubUrl(result.url, allowlist);
  const errorClass = serverError ? "HTTPError" : "TimeoutError";
  const failure = serverError ? `HTTP ${result.status}` : "timed out";
  const durationMs = Math.max(0, result.endTime - result.startTime);
  const outcome = handleError(
    `${result.method} ${url}: ${failure}`,
    undefined, undefined, undefined, undefined,
    // The same duration rides the params, where `beforeError` can redact it.
    // The span is built from the field below, which the hook cannot reach.
    { request: {
      url,
      method: result.method,
      ...(result.status === undefined ? {} : { status: result.status }),
      duration_ms: durationMs,
    } },
    errorClass,
    {
      trace_id: result.trace.traceId,
      span_id: result.trace.spanId,
      start_time_ms: result.startTime,
      duration_ms: durationMs,
    },
    result.endTime,
  );
  const reason = result.failureReason;
  if (!trackable(reason)) return;
  if (outcome === "sent") {
    reportedRequestErrors.add(reason);
  } else if (outcome === "suppressed") {
    // Held back, not rejected, so the hold lasts only as long as the gate that
    // caused it could still be in effect.
    suppressedRequestErrors.set(reason, Date.now() + SUPPRESSION_HOLD_MS);
  }
}

/** `sent` reached transport. `suppressed` means the SDK held it back for
 * volume, so the caller must not let the same object through another route.
 * `dropped` means a gate or the host rejected it, and the host's own error
 * still deserves its usual path. */
type ErrorOutcome = "sent" | "suppressed" | "dropped";

function handleError(
  message: string,
  filename?: string,
  lineno?: number,
  colno?: number,
  stack?: string,
  context?: Record<string, unknown>,
  errorClass?: string,
  trace?: ErrorTrace,
  occurredAt?: number,
): ErrorOutcome {
  if (!config.enabled) return "dropped";

  // Self-protection: ignore errors from our own SDK to prevent feedback loops
  if (isOwnError(filename, stack)) return "dropped";

  // Cross-origin script errors surface as an opaque "Script error." with no
  // stack and no usable location — the browser withholds the detail unless the
  // script is served with `crossorigin="anonymous"` + CORS headers. These can't
  // be symbolicated and carry zero actionable information, so drop them rather
  // than flood the stream with indistinguishable noise.
  if (message === "Script error." && !stack) return "dropped";

  // Sample rate check
  // Sampling is a volume gate, so a second route must not roll again: that
  // would report the failure at s + (1-s)s rather than s, and the second roll
  // produces a bare error with no trace and no endpoint.
  if (config.sampleRate < 1.0 && Math.random() >= config.sampleRate) return "suppressed";

  // Global rate limit — checked before the beforeError hook, the error
  // breadcrumb, the O(n) dedupe scan, and the network send, so a storm caps
  // the per-error work too, not just the wire volume.
  if (rateLimited(Date.now())) return "suppressed";

  // beforeError hook — early-pipeline. Runs before any side effect (error
  // breadcrumb, lastErrorTimestamp, dedupe slot, payload construction,
  // replay post-error tail). Returning null drops the error completely:
  // no breadcrumb pollution, no dedupe budget consumed, no replay
  // triggered. Mutating fields on the returned object propagates into the
  // eventual payload.
  const incoming: IncomingError = {
    message,
    error_class: errorClass,
    filename,
    lineno,
    colno,
    stack,
    context,
  };
  // A null return drops the error: no breadcrumb, no dedupe slot, no send.
  const hookResult = applyHook(beforeErrorHook, incoming);

  // beforeError is sync only. A Promise return would otherwise pass the
  // truthy check and the SDK would proceed treating the Promise as fields —
  // silent breakage. Detect it, drop the error, and log loudly so a host
  // developer can grep for the message.
  if (hookResult && typeof (hookResult as { then?: unknown }).then === "function") {
    logError(
      "beforeError returned a Promise. Async beforeError is not supported; " +
      "the error was dropped. Move async work outside the hook (e.g. perform " +
      "it before calling captureError).",
    );
    return "dropped";
  }
  if (!hookResult) return "dropped";
  const effective: IncomingError = hookResult;

  const now = Date.now();
  lastErrorTimestamp = now;

  // Error breadcrumb
  addBreadcrumb({
    timestamp: occurredAt ?? now,
    category: "error",
    message: effective.message.slice(0, 200),
  });

  // Deduplication: first 5 occurrences sent, 6+ suppressed
  const dedupeKey = dedupeKeyFor(effective.message, effective.stack);
  if (checkDedupe(dedupeKey, now)) return "suppressed";

  const payload: BrowserError = {
    type: "error",
    timestamp: occurredAt ?? now,
    // Already filtered (UX-only categories excluded) and capped to 25 by
    // the error-context ring buffer in breadcrumbs.ts.
    breadcrumbs: getErrorBreadcrumbs(),
    app_version: appVersion,
    ...effective,
  };

  sendError(toFrontendTransaction(payload, trace));

  // Session context is only consumed by subscribers, not the wire payload —
  // getSessionContext does real work (URL scrubbing, viewport/connection reads)
  // so skip it entirely when nobody's listening.
  if (errorListeners.length > 0) {
    payload.session = getSessionContext();
    // Ordinary JS error context is subscriber-only; request context is also
    // serialized as params above. Prune the subscriber copy after the hook
    // and dedupe gate, without mutating the host's original object.
    if (payload.context) {
      payload.context = pruneRecordForJson(payload.context);
    }
    for (const l of errorListeners) {
      try { l(payload); } catch { /* don't break the chain */ }
    }
  }
  return "sent";
}

// Map our internal BrowserError to the AppSignal `FrontendTransaction` wire
// shape consumed by the processor's frontend_errors pipeline. `revision` is
// the matchup key with sourcemaps uploaded out-of-band (S3 keyed by
// site_id + revision); without it stacks land unsymbolicated.
function toFrontendTransaction(error: BrowserError, trace?: ErrorTrace): FrontendTransaction {
  return {
    // Only an observed request failure carries the request's identity.
    // Ordinary JavaScript errors receive independent IDs during ingestion.
    ...trace,
    // Server expects unix seconds, not milliseconds.
    timestamp: Math.floor(error.timestamp / 1000),
    namespace: "browser",
    // The action groups the errors. A template such as "/users/:id" prevents
    // one error group for each ID in the URL.
    action: getRouteTemplate() || stripTrailingSlash(location.pathname),
    revision: error.app_version,
    service_name: serviceName,
    ...(trace && error.context ? { params: pruneRecordForJson(error.context) } : {}),
    error: {
      name: error.error_class || "Error",
      message: error.message,
      backtrace: error.stack ? error.stack.split("\n") : [],
    },
    breadcrumbs: error.breadcrumbs.map(toTransactionBreadcrumb),
    // Error tags are exactly what the host set via setTags (already coerced and
    // capped). The SDK injects no identity of its own — user identity rides the
    // session stream, not error tags; a host that wants user on errors sets it
    // explicitly via setTags.
    tags: getTags(),
    // The full href can contain a token or an OAuth fragment.
    environment: { url: scrubPageUrl(location.href, allowlist) },
    user_agent: navigator.userAgent,
  };
}

function toTransactionBreadcrumb(b: Breadcrumb): TransactionBreadcrumb {
  const data = b.data ?? {};
  return {
    timestamp: Math.floor(b.timestamp / 1000),
    category: b.category,
    action: actionForBreadcrumb(b.category, data),
    message: b.message,
    metadata: data,
  };
}

// `action` on a breadcrumb is a category-specific primary identifier. For
// clicks it's the CSS selector; for navigation it's the destination URL;
// for network/request it's the request URL; and so on. We pick from the
// breadcrumb's `data` rather than introducing new fields at capture time.
function actionForBreadcrumb(
  category: string,
  data: Record<string, unknown>,
): string {
  switch (category) {
    case "navigation":
      return String(data.to ?? data.url ?? "");
    case "click":
    case "rage_click":
    case "dead_click":
    case "error_click":
      return String(data.selector ?? "");
    case "network":
      return String(data.url ?? "");
    case "console":
      return String(data.level ?? "");
    case "visibility":
    case "tab":
      return String(data.state ?? "");
    default:
      // Includes "error", "long_task", "scroll_depth", and manual
      // breadcrumbs. Manual callers can put their own primary identifier
      // under `data.action` if they have one.
      return String(data.action ?? "");
  }
}

const SDK_MARKERS = ["@appsignal/browser", "browser.umd.js", "browser.esm.js"];

function isOwnError(filename?: string, stack?: string): boolean {
  const haystack = (filename || "") + (stack || "");
  return SDK_MARKERS.some((marker) => haystack.includes(marker));
}

// Non-Error promise rejections (`Promise.reject({ code: 500, detail: "…" })`)
// would collapse to "[object Object]" under String() — losing all detail and
// making every distinct object rejection dedupe to the same key. JSON-stringify
// objects so the payload survives; fall back to String() for primitives and for
// values JSON can't handle (circular refs, BigInt).
//
// "{}" names nothing, so every object rejection would share one dedupe key —
// fall back to the value's own string form, then its class name.
function stringifyReason(reason: unknown): string {
  if (reason === null || typeof reason !== "object") return String(reason);

  let json: string | undefined;
  try {
    json = JSON.stringify(reason);
    if (json && json !== "{}") return json;
  } catch {
    // Circular refs, BigInt.
  }

  try {
    const text = String(reason);
    if (!text.startsWith("[object ")) return text;
  } catch {
    // A null-prototype object has no toString to call.
  }

  const className = reason.constructor?.name;
  return className && className !== "Object" ? className : (json ?? "[object Object]");
}

function dedupeKeyFor(message: string, stack?: string): string {
  const frames = stack?.split("\n").slice(1, 4).map(f => f.trim()).join("|") || "";
  return `${message}|${frames}`;
}

function checkDedupe(key: string, now: number): boolean {
  // Clean old entries
  while (dedupeWindow.length > 0 && now - dedupeWindow[0].firstSeen > DEDUPE_WINDOW_MS) {
    dedupeWindow.shift();
  }

  const existing = dedupeWindow.find((e) => e.key === key);
  if (existing) {
    existing.count++;
    // First 5 occurrences are sent, 6+ are suppressed
    return existing.count > DEDUPE_MAX_COUNT;
  }

  dedupeWindow.push({ key, count: 1, firstSeen: now });
  return false;
}
